import { deriveExportAssetCssUrl } from "../../Uitls/exportAssetFileName";
import {
  buildFillOnlySvgSrc,
  buildStrokeOnlySvgSrc,
  getShapeStrokeZIndex,
  hasVisibleShapeBorder,
  isShapeMatchingCanvasSize,
} from "../../Uitls/shapeStrokeOverlay";

let CssCollect = "";
let DomObj = "";
let atomId = "";
let img = [];
let atomUrl = "";
let trackHeight = [];
let thumbHeight = 0;
const normalizeFontFamily = (ff) => {
  if (!ff) return "Arial";

  return String(ff)
    .split(",")
    .map((f) => f.trim())
    .filter(Boolean)
    .map((f) => (/\s/.test(f) && !/^["'].*["']$/.test(f) ? `"${f}"` : f))
    .join(", ");
};

const HIT_AREA_MIN_SIZE = 5;

const toFiniteNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizeTargetLinkForExport = (value) => {
  if (typeof value !== "string") return "";
  const trimmedValue = value.trim();
  if (
    !trimmedValue ||
    trimmedValue === "http://" ||
    trimmedValue === "https://"
  ) {
    return "";
  }
  return new RegExp("^https?://", "i").test(trimmedValue) ? trimmedValue : "";
};

const getScrollbarChildInitialState = (component) => {
  const styles = component?.componentStyles || {};
  const authoredOpacity = styles.compOpacity;
  const numericOpacity = Number(authoredOpacity);
  const hasAuthoredOpacity =
    authoredOpacity !== undefined &&
    authoredOpacity !== null &&
    authoredOpacity !== "" &&
    Number.isFinite(numericOpacity);

  return {
    x: styles.left,
    y: styles.top,
    width: styles.width,
    height: styles.height,
    rotation: styles.rotation ?? 0,
    transform: styles.scale ?? 1,
    opacity: styles.hideBoolean
      ? 0
      : hasAuthoredOpacity
        ? numericOpacity / 100
        : 1,
  };
};

const hasValidCSSValue = (value) => 
  value !== undefined && 
  value !== null && 
  value !== "" && 
  value !== "undefined" && 
  value !== "null";


const cssProps = (property, value, unit = "") => 
  hasValidCSSValue(value) ? `${property}: ${value}${unit};` : "";

const buildTransform = (rotation, scale) => {
  const transforms = [];

  if(hasValidCSSValue(rotation)){
    transforms.push(`rotate(${rotation}deg)`);
  }

  if(hasValidCSSValue(scale)){
    transforms.push(`scale(${scale})`);
  }

  return transforms.length ? `transform: ${transforms.join(" ")};` : "";
}

const buildPadding = (top, right, bottom, left) => {
  if (![top, right, bottom, left].every(hasValidCSSValue)) {
    return "";
  }

  return `padding: ${top}px ${right}px ${bottom}px ${left}px;`;
};

const buildBorderRadius = (tl, tr, br, bl) => {
  if (![tl, tr, br, bl].every(hasValidCSSValue)) {
    return "";
  }

  return `border-radius: ${tl}px ${tr}px ${br}px ${bl}px;`;
};

const removeDuplicateCss = (rules) => {
  const cssMap = new Map();

  rules.filter(Boolean).forEach((rule) => {
    const index = rule.indexOf(":");
    if(index === -1) return;

    const property = rule.slice(0, index).trim();
    cssMap.set(property, rule);
  });

  return[...cssMap.values()].join("\n ");
}

const buildShapeStrokeFrame = ({
  layerId,
  styles,
  newLeft,
  newTop,
  newOpacity,
  newRotation,
  newTransform,
  kind,
  shapeType,
  svgSrc,
}) => {
  const strokeId = `${layerId}-shape-stroke`;
  const strokeZ = getShapeStrokeZIndex(styles.zIndex);

  if (kind === "css") {
    if (!hasVisibleShapeBorder(styles.border)) {
      return { dom: "", css: "" };
    }
    const radiusRule =
      shapeType === "circleShape"
        ? cssProps("border-radius", styles.borderRadius)
        : buildBorderRadius(
            styles.borderTopLeftRadius,
            styles.borderTopRightRadius,
            styles.borderBottomRightRadius,
            styles.borderBottomLeftRadius,
          );
    const strokeStyles = removeDuplicateCss([
      cssProps("position", "absolute"),
      cssProps("width", styles.width, "px"),
      cssProps("height", styles.height, "px"),
      cssProps("box-sizing", "border-box !important"),
      cssProps("top", newTop, "px"),
      cssProps("left", newLeft, "px"),
      cssProps("z-index", strokeZ),
      cssProps("background-color", "transparent"),
      cssProps("pointer-events", "none"),
      cssProps("border", styles.border),
      radiusRule,
      buildTransform(newRotation, newTransform),
      cssProps("opacity", newOpacity),
    ]);
    return {
      dom: `<div id="${strokeId}" data-shape-stroke="true"></div>`,
      css: strokeStyles
        ? `\n#${strokeId} {\n                ${strokeStyles}\n              }`
        : "",
    };
  }

  const strokeSrc = buildStrokeOnlySvgSrc(svgSrc);
  if (!strokeSrc) return { dom: "", css: "" };
  const strokeStyles = removeDuplicateCss([
    cssProps("position", "absolute"),
    cssProps("width", styles.width, "px"),
    cssProps("height", styles.height, "px"),
    cssProps("top", newTop, "px"),
    cssProps("left", newLeft, "px"),
    cssProps("z-index", strokeZ),
    cssProps("pointer-events", "none"),
    cssProps("background-color", "transparent"),
    buildTransform(newRotation, newTransform),
    cssProps("opacity", newOpacity),
  ]);
  return {
    dom: `<div id="${strokeId}" data-shape-stroke="true"><img src="${strokeSrc}" style="width:100%;height:100%;display:block;" alt=""/></div>`,
    css: strokeStyles
      ? `\n#${strokeId} {\n                ${strokeStyles}\n              }`
      : "",
  };
};

const sanitizeHitAreaKeyframesForExport = (
  hitArea,
  normalizedWidth,
  normalizedHeight,
  imageWidth,
  imageHeight
) => {
  const sourceKeyframes = Array.isArray(hitArea?.keyframes)
    ? hitArea.keyframes
    : Array.isArray(hitArea?.keyFrames)
      ? hitArea.keyFrames
      : [];

  const keyframes = sourceKeyframes
    .map((keyframe, keyframeIndex) => {
      const keyframeWidth = clampNumber(
        Math.round(toFiniteNumber(keyframe?.width, normalizedWidth)),
        HIT_AREA_MIN_SIZE,
        imageWidth
      );
      const keyframeHeight = clampNumber(
        Math.round(toFiniteNumber(keyframe?.height, normalizedHeight)),
        HIT_AREA_MIN_SIZE,
        imageHeight
      );
      const rawTime = toFiniteNumber(
        keyframe?.time,
        toFiniteNumber(keyframe?.marker, 0)
      );

      const keyframeX =
        keyframe?.x !== undefined
          ? clampNumber(
              Math.round(toFiniteNumber(keyframe.x, toFiniteNumber(hitArea?.x, 0))),
              0,
              Math.max(0, imageWidth - keyframeWidth)
            )
          : undefined;
      const keyframeY =
        keyframe?.y !== undefined
          ? clampNumber(
              Math.round(toFiniteNumber(keyframe.y, toFiniteNumber(hitArea?.y, 0))),
              0,
              Math.max(0, imageHeight - keyframeHeight)
            )
          : undefined;

      return {
        id: keyframe?.id || `hit-area-kf-${keyframeIndex + 1}`,
        time: Number(Math.max(0, rawTime).toFixed(2)),
        width: keyframeWidth,
        height: keyframeHeight,
        ...(keyframeX !== undefined ? { x: keyframeX } : {}),
        ...(keyframeY !== undefined ? { y: keyframeY } : {}),
      };
    })
    .filter((frame) => Number.isFinite(frame.time))
    .sort((firstFrame, secondFrame) => firstFrame.time - secondFrame.time);

  if (keyframes.length) return keyframes;

  return [
    {
      id: "hit-area-kf-start",
      time: 0,
      x: Math.round(toFiniteNumber(hitArea?.x, 0)),
      y: Math.round(toFiniteNumber(hitArea?.y, 0)),
      width: normalizedWidth,
      height: normalizedHeight,
    },
  ];
};

const sanitizeHitAreasForExport = (hitAreas = [], styles = {}) => {
  if (!Array.isArray(hitAreas) || !hitAreas.length) return [];

  const imageWidth = Math.max(1, Math.round(toFiniteNumber(styles?.width, 1)));
  const imageHeight = Math.max(1, Math.round(toFiniteNumber(styles?.height, 1)));

  return hitAreas
    .map((hitArea, index) => {
      if (hitArea?.hideBoolean) return null;
      // Keep the raw link (can be empty – overlay is still rendered for
      // position preview; click tags are only registered when URL is valid).
      const targetLink = normalizeTargetLinkForExport(hitArea?.targetLink) || "";

      const normalizedWidth = clampNumber(
        Math.round(toFiniteNumber(hitArea?.width, HIT_AREA_MIN_SIZE)),
        HIT_AREA_MIN_SIZE,
        imageWidth
      );
      const normalizedHeight = clampNumber(
        Math.round(toFiniteNumber(hitArea?.height, HIT_AREA_MIN_SIZE)),
        HIT_AREA_MIN_SIZE,
        imageHeight
      );
      const normalizedX = clampNumber(
        Math.round(toFiniteNumber(hitArea?.x, 0)),
        0,
        Math.max(0, imageWidth - normalizedWidth)
      );
      const normalizedY = clampNumber(
        Math.round(toFiniteNumber(hitArea?.y, 0)),
        0,
        Math.max(0, imageHeight - normalizedHeight)
      );

      const timelineStart = Number(
        Math.max(0, toFiniteNumber(hitArea?.timelineStart, 0)).toFixed(2)
      );
      const timelineEnd = Number(
        Math.max(
          timelineStart + 0.05,
          toFiniteNumber(hitArea?.timelineEnd, timelineStart + 30)
        ).toFixed(2)
      );

      return {
        id: hitArea?.id || `hit-area-${index + 1}`,
        name: String(hitArea?.name ?? "").trim(),
        sourceImageWidth: imageWidth,
        sourceImageHeight: imageHeight,
        x: normalizedX,
        y: normalizedY,
        width: normalizedWidth,
        height: normalizedHeight,
        targetLink,
        timelineStart,
        timelineEnd,
        keyframes: sanitizeHitAreaKeyframesForExport(
          hitArea,
          normalizedWidth,
          normalizedHeight,
          imageWidth,
          imageHeight
        ),
      };
    })
    .filter(Boolean);
};

const serializeHitAreasForExport = (hitAreas) => {
  if (!Array.isArray(hitAreas) || !hitAreas.length) return "";
  try {
    return encodeURIComponent(JSON.stringify(hitAreas));
  } catch (error) {
    return "";
  }
};

/**
 * Renders each hit area as a positioned <div> inside the image wrapper.
 * Each overlay carries a stable `id` so GSAP can target it like any other layer.
 * data-base-width/height store the design-time image size so export playback can
 * scale overlays when the parent image's width/height is animated.
 */
const renderHitAreaOverlays = (exportHitAreas, imageStyles = {}) => {
  if (!Array.isArray(exportHitAreas) || !exportHitAreas.length) return "";

  const baseWidth = Math.max(1, Number(imageStyles?.width) || 1);
  const baseHeight = Math.max(1, Number(imageStyles?.height) || 1);

  const overlayDivs = exportHitAreas
    .map((ha) => {
      const hasLink = Boolean(ha.targetLink);
      const timelineStart = Number(ha.timelineStart);
      const timelineEnd = Number(ha.timelineEnd);
      const startAttr = Number.isFinite(timelineStart) ? timelineStart : 0;
      const endAttr = Number.isFinite(timelineEnd) ? timelineEnd : 30;
      return (
        `<div` +
        ` id="${ha.id}"` +
        ` class="imc-hit-area-overlay"` +
        ` data-timeline-start="${startAttr}"` +
        ` data-timeline-end="${endAttr}"` +
        ` data-has-link="${hasLink ? "1" : "0"}"` +
        ` style="position:absolute;cursor:${hasLink ? "pointer" : "default"};box-sizing:border-box;pointer-events:auto;"` +
        `></div>`
      );
    })
    .join("");

  return (
    `<div class="imc-hit-area-overlays"` +
    ` data-base-width="${baseWidth}"` +
    ` data-base-height="${baseHeight}"` +
    ` style="position:absolute;top:0;left:0;width:${baseWidth}px;height:${baseHeight}px;pointer-events:none;z-index:2147483647;overflow:hidden;transform-origin:0 0;">` +
    overlayDivs +
    `</div>`
  );
};

/** Initial CSS for hit area overlays — same source as regular layers (animation[0]). */
const buildHitAreaCssRules = (exportHitAreas, animationObject = {}) => {
  if (!Array.isArray(exportHitAreas) || !exportHitAreas.length) return "";

  return exportHitAreas
    .map((ha) => {
      const set = animationObject[ha.id]?.animation?.[0] || {};
      const hasSet = Object.keys(set).length > 0;
      const kf0 = ha.keyframes?.[0];

      const x = hasSet && set.x !== undefined ? set.x : kf0?.x !== undefined ? kf0.x : ha.x;
      const y = hasSet && set.y !== undefined ? set.y : kf0?.y !== undefined ? kf0.y : ha.y;
      const w =
        hasSet && set.width !== undefined
          ? set.width
          : kf0?.width !== undefined
            ? kf0.width
            : ha.width;
      const h =
        hasSet && set.height !== undefined
          ? set.height
          : kf0?.height !== undefined
            ? kf0.height
            : ha.height;
      const opacity = hasSet && set.opacity !== undefined ? set.opacity : 0;
      const rotation = hasSet && set.rotation !== undefined ? set.rotation : 0;
      const scale = hasSet && set.transform !== undefined ? set.transform : 1;

      return (
        `\n#${ha.id}{` +
        `position:absolute;` +
        `left:${x}px;` +
        `top:${y}px;` +
        `width:${w}px;` +
        `height:${h}px;` +
        `opacity:${opacity};` +
        `transform:rotate(${rotation}deg)scale(${scale});` +
        `}`
      );
    })
    .join("");
};

export function downLoad({
  exportData,
  layerCompData,
  assetId,
  canvasDimension,
  bgColorCanvas,
  animationObject = {},
  flag,
  popUpOptions,
  toggleAnimatedBanner,
}) {
  CssCollect = "";
  img = [];
  DomObj = "";
  atomId = "";
  atomUrl = "";
  trackHeight = [];
  let clickHandlers = [];
  if (layerCompData) {
    layerCompData.forEach((data) => {
      let getDom = getDOM(
        data,
        animationObject[data.id]?.animation[0],
        popUpOptions,
        flag,
        undefined,
        exportData,
        animationObject,
        canvasDimension
      );

      DomObj = DomObj + getDom.DOMConstruct;
      CssCollect = CssCollect + getDom.CssCollect;
      trackHeight = getDom.trackHeight;
      thumbHeight = getDom.thumbHeight;
    });
  }

  layerCompData.forEach((data) => {
    let innerContent = "";
    let isDom = true;
    if (data.scrollbarComponents) {
      data.scrollbarComponents.forEach((comp) => {
        const isScrollbarLink =
          data?.componentStyles.targetLink !== "https://" &&
          data?.componentStyles.targetLink !== "http://" &&
          data?.componentStyles.targetLink !== undefined &&
          data?.componentStyles.targetLink !== "";
        const getDom = getDOM(
          comp,
          getScrollbarChildInitialState(comp),
          undefined,
          undefined,
          isScrollbarLink,
          exportData,
        );

        if (getDom) {
          innerContent += getDom.DOMConstruct;
          CssCollect += getDom.CssCollect || "";
          trackHeight = getDom.trackHeight;
          thumbHeight = getDom.thumbHeight;
        } else {
          isDom = false;
        }
      });
    }
    if (isDom) {
      DomObj = DomObj.replace(
        `<div id='${data.id}-inner' class='inner-div'></div>`,
        `<div id='${data.id}-inner' class='inner-div'>${innerContent}</div>`,
      );
    }
  });
  let getLayerData;
  for (let i = 0; i < layerCompData?.length; i++) {
    getLayerData = i;
  }

  CssCollect =
    CssCollect +
    `\n#canvas-page{display:none; position:absolute; width:${canvasDimension?.width}px; height:${canvasDimension?.height}px;background:rgba(${bgColorCanvas?.r}, ${bgColorCanvas?.g}, ${bgColorCanvas?.b}, ${bgColorCanvas?.a});font-family: arial;font-size:10px; line-height:1.25;overflow:hidden} .button-div {
    word-break: break-word;
    text-align: center;  
  }
  .button-style {
    background-color: rgb(65, 65, 65);
    border: none;
    border-radius: 3px;
    color: rgb(255, 255, 255);
    display: inline-block;
    padding:5px 10px;
    line-height: 0;
    outline:none;
    position: absolute;
    cursor:pointer;
    overflow:hidden;
  }
  #track{
    overflow-x: hidden;
  }
  .button-style p{
    padding:0;
    margin:0;
  }
  #canvas-page button ul,
  #canvas-page button ol {
  display: block;
  width: 100%;
  max-width: 100%;
  margin: 0 !important;
  padding-left: 15px !important;
  box-sizing: border-box;
  list-style-position: outside;
}
  #canvas-page button li {
  display: list-item;
}
  .pentagonImage {
    transform: rotate(0deg);
  }
 
.skew1 { 
  background-color: transparent;
  width: 677px;
  height: 378px;
  position: absolute;
  top: -1px;
  left: -189px;
  overflow: hidden;
  box-shadow: 1px 1px 50px rgba(0, 0, 0, 0.8);
  -ms-transform: skewX(-45deg);
  -webkit-transform: skewX(-45deg);
  transform: skewX(-45deg);
}
.skew1imgbg {
  background: url(${exportData ? deriveExportAssetCssUrl(layerCompData[getLayerData]?.componentStyles?.background) : layerCompData[getLayerData]?.componentStyles?.background})
  no-repeat;
  width: 300px;
  height: 493px;
  position: absolute;
  top: 0px;
  left: 245px;
  -ms-transform: skewX(+45deg);
  -webkit-transform: skewX(+45deg);
  transform: skewX(+45deg);
}
.textProps ol {
  margin-top:0 !important;
}
.textProps ul {
  margin-top:0 !important;
}
.textProps ul{
  padding-left: 20px;
}
.textProps ol{
  padding-left: 20px;
}

.headingTextProps ul {
  padding-left: 24px;
  list-style-position: inside;
  margin-left: 0 !important;
  padding-left: 0 !important;
}

.headingTextProps ol {
  padding-left: 44px;
  list-style-position: inside;
  margin-left: 0 !important;
  padding-left: 0 !important;
}
.top_arrow{
 margin-top:1.6px;
}
.bottom_arrow{
  margin-bottom:1.6px;
}
  `;

  if (layerCompData) {
    layerCompData.forEach((data) => {
      if (data.componentStyles?.hideBoolean) return;
      const url = data?.componentStyles?.targetLink;
      const isValid =
        url &&
        url !== "https://" &&
        url !== "http://" &&
        url !== "undefined" &&
        url !== "";
      if (isValid && data.id) {
        clickHandlers.push({ id: data.id, url });
      }

      if (data.scrollbarComponents) {
        data.scrollbarComponents.forEach((comp) => {
          if (comp.componentStyles?.hideBoolean) return;
          const compUrl = comp?.componentStyles?.targetLink;
          const isCompUrlValid =
            compUrl &&
            compUrl !== "https://" &&
            compUrl !== "http://" &&
            compUrl !== "undefined" &&
            compUrl !== "";
          if (isCompUrlValid && comp.id) {
            clickHandlers.push({ id: comp.id, url: compUrl });
          }
        });
      }

      const hitAreas = data?.componentStyles?.hitAreas;
      if (Array.isArray(hitAreas)) {
        hitAreas.forEach((hitArea) => {
          if (hitArea?.hideBoolean) return;
          const hitAreaUrl = normalizeTargetLinkForExport(hitArea?.targetLink);
          if (hitAreaUrl && hitArea?.id) {
            clickHandlers.push({ id: hitArea.id, url: hitAreaUrl });
          }
        });
      }
    });
  }

  return {
    DomObj,
    CssCollect,
    assetId,
    atomId,
    img,
    atomUrl,
    trackHeight,
    thumbHeight,
    clickHandlers,
  };
}

export function getDOM(
  layerData,
  set = {},
  popUpOptions,
  flag,
  isScrollbarLink,
  exportData,
  animationObject = {},
  canvasDimension
) {
  let DOMConstruct = "";
  let CssCollect = "";

  let styles = layerData.componentStyles;

  let newLeft, newTop, newOpacity, newRotation, newTransform;
  let cursorPointer =
    styles.targetLink &&
    styles.targetLink !== "http://" &&
    styles.targetLink !== "https://"
      ? "pointer"
      : "default";

  if (layerData.componentStyles.hideBoolean)
    return { DOMConstruct, CssCollect, trackHeight, thumbHeight };
  switch (layerData.componentType) {
    case "Scrollbar":
      if (Object.keys(set).length) {
        newLeft = set.x;
        newTop = set.y;
        newOpacity =
          styles.compOpacity === 100 ? set?.opacity : styles.compOpacity / 100;
        newRotation = set?.rotation;
        newTransform = set?.transform;
      } else {
        newLeft = styles.left;
        newTop = styles.top;
        newOpacity =
          styles.compOpacity === 100 ? set?.opacity : styles.compOpacity / 100;
        newRotation = set?.rotation;
        newTransform = set?.transform;
      }

      trackHeight.push({
        id: layerData.id,
        autoScroll: false,
      });
      DOMConstruct = `<div id='${layerData.id}' class='custom-scrollbar'>
                        <div id='${layerData.id}-inner' class='inner-div'></div>
                        </div>  `;

      const scrolbarStyles = removeDuplicateCss([
        cssProps("position", "absolute"),
        cssProps("width", styles.width, "px"),
        cssProps("height", styles.height, "px"),
        cssProps("top", newTop, "px"),
        cssProps("left", newLeft, "px"),
        cssProps("z-index", styles.zIndex),
        cssProps("background-color", styles.backgroundColor),
        cssProps("border", styles.border),
        buildBorderRadius(
          styles.borderTopLeftRadius,
          styles.borderTopRightRadius,
          styles.borderBottomRightRadius,
          styles.borderBottomLeftRadius
        ),
        buildPadding(
          styles.paddingTop,
          styles.paddingRight,
          styles.paddingBottom,
          styles.paddingLeft
        ),
        buildTransform(newRotation, newTransform),
        cssProps("opacity", newOpacity),
        cssProps("overflow-y", "scroll"),
        cssProps("overflow-x", "hidden")
      ]);

      const customScrollbarStyles = removeDuplicateCss([
        styles.targetLink ? "cursor: pointer !important;" : ""
      ]);

      const scrollbarTrackStyles = removeDuplicateCss([
        cssProps("background", styles.scrollThumbBgColor)
      ]);

      const scrollbarThumbStyles = removeDuplicateCss([
        cssProps("background", styles.scrollThumbColor),
        cssProps("border-radius", styles.scrollBorderRadius)
      ]);

      const innerStyles = removeDuplicateCss([
        cssProps("width", styles.width, "px"),
        cssProps("height", styles.scrollbarHeight, "px")
      ]);

      const cssBlock = [
        `#${layerData.id} {
          ${scrolbarStyles}
        }`,
        
        customScrollbarStyles 
          ? `.custom-scrollbar * {
              ${customScrollbarStyles}
            }`
          : "",

        `#${layerData.id}::-webkit-scrollbar {
          width:0.5em;
        }`,

        scrollbarTrackStyles 
          ? `#${layerData.id}::-webkit-scrollbar-track {
              ${scrollbarTrackStyles}
            }`
          : "",

        scrollbarThumbStyles
          ? `#${layerData.id}::-webkit-scrollbar-thumb {
              ${scrollbarThumbStyles}
            }`
          : "",

        `@-moz-document url-prefix() {
          #${layerData.id} {
            scrollbar-color: ${styles.scrollThumbColor} ${styles.scrollThumbBgColor};
            scrollbar-width: thin;
          }
        }
        `,

        innerStyles
          ? `#${layerData.id}-inner {
              ${innerStyles}
            }`
          : "",
      ].filter(Boolean).join("\n ");
      CssCollect += `\n${cssBlock}`;
      break;

  case "Text": {
    if (Object.keys(set).length) {
      newLeft = set?.x;
      newTop = set?.y;
      newOpacity = set?.opacity;
      newRotation = set?.rotation;
      newTransform = set?.transform;
    } else {
      newLeft = styles.left;
      newTop = styles.top;
      newOpacity = styles?.opacity;
      newRotation = styles?.rotation;
      newTransform = styles?.transform;
    }
  
    if (!layerData.componentStyles.toggleScroll) {
      const textDecorationvalue = styles?.underlineClick
        ? styles?.strikeThroughClicked
          ? `${styles?.textDecUnderline} ${styles?.textDecoration}`
          : styles?.textDecUnderline
        : styles?.textDecoration;
  
      DOMConstruct = `
        <div id="${layerData.id}" class="textProps${
          layerData.textComponentType?.addContent === "AddHeading"
            ? " headingTextProps"
            : ""
        }">
          ${layerData.froalaText}
        </div>
      `;
  
      const textStyles = removeDuplicateCss([
        cssProps("position", "absolute"),
        cssProps("width", styles.width, "px"),
        cssProps("height", styles.height, "px"),
        cssProps("top", newTop, "px"),
        cssProps("left", newLeft, "px"),
        cssProps("font-weight", styles.fontWeight),
        cssProps(
          "font-family",
          normalizeFontFamily(styles.fontFamily)
        ),
        cssProps("color", styles.color),
        cssProps("line-height", styles.lineHeight),
        cssProps("z-index", styles.zIndex),
        cssProps("letter-spacing", styles.letterSpacing),
        cssProps("font-size", styles.fontSize, "px"),
        cssProps("word-wrap", "break-word"),
        buildTransform(newRotation, newTransform),
        cssProps("opacity", newOpacity),
        cssProps("cursor", cursorPointer),
        cssProps("background-color", styles.textBgColor),
        cssProps("overflow", "hidden"),
        cssProps("font-style", styles?.fontStyle),
        cssProps("text-transform", styles?.textTransform),
        cssProps("text-decoration", textDecorationvalue),
        cssProps("text-align", styles?.textAlign),
      ]);
  
      const textCssBlock = textStyles
        ? `#${layerData.id} {
    ${textStyles}
  }`
        : "";
  
      CssCollect += textCssBlock ? `\n${textCssBlock}` : "";
    } else {
      const completeObj = {};
  
      completeObj.id = layerData.id;
      completeObj.thumbHeight = styles.thumbHeight;
      completeObj.trackHeight =
        styles.height -
        styles.arrowHeight * 2 -
        (styles.arrowHeight === 0 ? 0 : 2) -
        (styles.expandAndClose
          ? styles.expandClose?.expandHeight
          : 0);
      completeObj.autoScroll = styles.autoScroll;
      completeObj.arrowHeight = styles.arrowHeight;
      completeObj.compHeight = styles.height;
      completeObj.compTop = newTop;
      completeObj.expandHeight =
        styles?.expandClose?.expandHeight;
      completeObj.compLeft = newLeft;
      completeObj.compWidth = styles.width;
  
      trackHeight.push(completeObj);
  
      const textDecorationvalue = styles?.underlineClick
        ? styles?.strikeThroughClicked
          ? `${styles?.textDecUnderline} ${styles?.textDecoration}`
          : styles?.textDecUnderline
        : styles?.textDecoration;
  
      const textDecvalue =
        styles?.expandClose?.underlineClick
          ? styles?.expandClose?.strikeThroughClicked
            ? `${styles?.expandClose?.textDecUnderline} ${styles?.expandClose?.textDecoration}`
            : styles?.expandClose?.textDecUnderline
          : styles?.expandClose?.textDecoration;
  
      const expanCloseDIV = styles.expandAndClose
        ? `
          <div
            id="expand${layerData.id}"
            onClick="expandBanner('${layerData.id}')"
          >
            + ${styles.expandText}
          </div>
  
          <div
            id="close${layerData.id}"
            onClick="closeBanner('${layerData.id}')"
          >
            - ${styles.closeText}
          </div>
        `
        : "";
  
      const expandStyles = removeDuplicateCss([
        cssProps("height", styles.expandClose?.expandHeight, "px"),
        cssProps("font-weight", styles.expandClose?.fontWeight),
        cssProps("font-family", normalizeFontFamily(styles.expandClose?.fontFamily)),
        cssProps("color", styles.expandClose?.fontColorISI),
        cssProps("line-height", styles.expandClose?.lineHeight),
        cssProps("letter-spacing", styles.expandClose?.letterSpacing, "px"),
        cssProps("font-size", styles.expandClose?.fontSize, "px"),
        cssProps("background-color", styles.expandClose?.textBgColorISI),
        cssProps("text-align", "right"),
        cssProps("font-style", styles.expandClose?.fontStyle),
        cssProps("text-transform", styles.expandClose?.textTransform),
        cssProps("text-decoration", textDecvalue),
      ]);
  
      const closeStyles = removeDuplicateCss([
        cssProps("display", "none"),
        cssProps("height", styles.expandClose?.expandHeight, "px"),
        cssProps("font-weight", styles.expandClose?.fontWeight),
        cssProps("font-family", normalizeFontFamily(styles.expandClose?.fontFamily)),
        cssProps("color", styles.expandClose?.fontColorISI),
        cssProps("line-height", styles.expandClose?.lineHeight),
        cssProps("letter-spacing", styles.expandClose?.letterSpacing, "px"),
        cssProps("font-size", styles.expandClose?.fontSize, "px"),
        cssProps("background-color", styles.expandClose?.textBgColorISI),
        cssProps("text-align", "right"),
        cssProps("font-style", styles.expandClose?.fontStyle),
        cssProps("text-transform", styles.expandClose?.textTransform),
        cssProps("text-decoration", textDecvalue),
      ]);
  
      const expanCloseCSS = styles.expandAndClose
        ? [
            expandStyles
              ? `#expand${layerData.id} {
                  ${expandStyles}
                }`
              : "",
  
            closeStyles
              ? `#close${layerData.id} {
                  ${closeStyles}
                }`
              : "",
          ]
            .filter(Boolean)
            .join("\n\n")
        : "";
  
      const tempExpandClose = `
        <div id="${layerData.id}">
          ${expanCloseDIV}
  
          <div id="${layerData.id}ISIInfo">
            <div
              id="${layerData.id}arrowTop"
              class="top_arrow"
            ></div>
  
            <div
              id="scrollbar${layerData.id}"
              class="scrollbar"
            >
              <div
                id="${layerData.id}track"
                class="track"
              >
                <div
                  id="${layerData.id}thumb"
                  class="thumb"
                ></div>
              </div>
            </div>
  
            <div
              id="${layerData.id}viewport"
              class="viewport"
            >
              <div
                id="${layerData.id}overview"
                class="overview textProps${
                  layerData.textComponentType?.addContent === "AddHeading"
                    ? " headingTextProps"
                    : ""
                }"
              >
                ${layerData.froalaText}
              </div>
            </div>
  
            <div
              id="${layerData.id}arrowBottom"
              class="bottom_arrow"
            ></div>
          </div>
        </div>
      `;
  
      const tempScroll = `
        <div id="${layerData.id}">
          <div id="${layerData.id}ISIInfo">
            <div
              id="${layerData.id}arrowTop"
              class="top_arrow"
            ></div>
  
            <div
              id="scrollbar${layerData.id}"
              class="scrollbar"
            >
              <div
                id="${layerData.id}track"
                class="track"
              >
                <div
                  id="${layerData.id}thumb"
                  class="thumb"
                ></div>
              </div>
            </div>
  
            <div
              id="${layerData.id}viewport"
              class="viewport"
            >
              <div
                id="${layerData.id}overview"
                class="overview textProps${
                  layerData.textComponentType?.addContent === "AddHeading"
                    ? " headingTextProps"
                    : ""
                }"
              >
                ${layerData.froalaText}
              </div>
            </div>
  
            <div
              id="${layerData.id}arrowBottom"
              class="bottom_arrow"
            ></div>
          </div>
        </div>
      `;
  
      if (styles?.expandAndClose) {
        DOMConstruct += tempExpandClose;
      } else {
        DOMConstruct += tempScroll;
      }
  
      const mainTextStyles = removeDuplicateCss([
        cssProps("position", "absolute"),
        cssProps("top", newTop, "px"),
        cssProps("width", styles.width, "px"),
        cssProps("height", styles.height, "px"),
        cssProps("left", newLeft, "px"),
        cssProps("z-index", styles.zIndex),
        buildTransform(newRotation, newTransform),
        cssProps("opacity", newOpacity),
        cssProps("cursor", cursorPointer),
        cssProps("background-color", styles.textBgColor),
        cssProps("font-style", styles?.fontStyle),
        cssProps("text-transform", styles?.textTransform),
        cssProps("text-decoration", textDecorationvalue),
        cssProps("text-align", styles?.textAlign),
      ]);
  
      const isiInfoStyles = removeDuplicateCss([
        cssProps("position", "relative"),
        cssProps("width", styles.width, "px"),
        cssProps(
          "height",
          styles.height +
            (styles.arrowHeight === 0 ? 0 : 2) -
            (styles.expandAndClose
              ? styles.expandClose?.expandHeight
              : 0),
          "px"
        ),
        cssProps("font-weight", styles.fontWeight),
        cssProps("font-family", normalizeFontFamily(styles.fontFamily)),
        cssProps("color", styles.color),
        cssProps("line-height", styles.lineHeight),
        cssProps("z-index", styles.zIndex),
        cssProps("letter-spacing", styles.letterSpacing, "px"),
        cssProps("font-size", styles.fontSize, "px"),
        cssProps("overflow", "hidden"),
        cssProps("background-color", styles.textBgColor),
      ]);
  
      const viewportStyles = removeDuplicateCss([
        cssProps("position", "relative"),
        cssProps("width", "100%"),
        cssProps("height", styles.height, "px"),
        cssProps("overflow", "hidden"),
        cssProps("top", 0, "px"),
        cssProps("left", 0, "px"),
        cssProps("word-break", "break-word"),
      ]);
  
      const overviewPaddingBottom =
        styles.expandAndClose &&
        hasValidCSSValue(
          styles?.expandClose?.lineHeight
        ) &&
        hasValidCSSValue(
          styles?.expandClose?.fontSize
        )
          ? styles.fontFamily === "Arial"
            ? styles.expandClose.lineHeight *
                styles.expandClose.fontSize +
              3
            : styles.expandClose.lineHeight *
                styles.expandClose.fontSize +
              10
          : undefined;
  
      const overviewStyles = removeDuplicateCss([
        cssProps("position", "relative"),
        cssProps("left", 0, "px"),
        cssProps("top", 0, "px"),
        cssProps(
          "padding-right",
          styles.trackWidth +
            2 +
            styles.thumbRight * 2,
          "px"
        ),
        cssProps(
          "padding-bottom",
          overviewPaddingBottom,
          "px"
        ),
      ]);
  
      const scrollbarStyles = removeDuplicateCss([
        cssProps(
          "background",
          "rgba(255,255,255,0.00)"
        ),
        cssProps("position", "absolute"),
        cssProps("top", styles.arrowHeight, "px"),
        cssProps(
          "margin-top",
          styles.arrowHeight > 0 ? 2 : 0,
          "px"
        ),
        cssProps(
          "right",
          styles.trackRight
            ? styles.trackRight + 0.2
            : 0,
          "px"
        ),
        cssProps("cursor", "pointer"),
        cssProps("z-index", 10),
        cssProps(
          "height",
          styles.height -
            styles.arrowHeight * 2 -
            (styles.arrowHeight === 0 ? 0 : 2) -
            (styles.expandAndClose
              ? styles?.expandClose?.expandHeight
              : 0),
          "px"
        ),
      ]);
  
      const trackStyles = removeDuplicateCss([
        cssProps("top", styles.arrowHeight, "px"),
        cssProps("width", styles.trackWidth, "px"),
        cssProps("background-color", styles.thumbColor),
        cssProps("border-radius", styles.trackRadius, "px"),
      ]);
  
      const thumbStyles = removeDuplicateCss([
        cssProps(
          "right",
          hasValidCSSValue(styles.thumbRight)
            ? -styles.thumbRight
            : undefined,
          "px"
        ),
        cssProps("border-radius", "inherit"),
        cssProps("cursor", "pointer"),
        cssProps("background-color", "black"),
        cssProps("background-position", "center"),
        cssProps(
          "background-image",
          hasValidCSSValue(styles.scrollThumbBg)
            ? `url("${styles.scrollThumbBg}")`
            : undefined
        ),
        cssProps("background-repeat", "no-repeat"),
        cssProps(
          "height",
          styles.textHeight > styles.height
            ? styles.thumbHeight
            : 0,
          "px"
        ),
        cssProps("width", styles.thumbWidth, "px"),
        cssProps("overflow", "hidden"),
        cssProps("position", "absolute"),
        cssProps("top", 0, "px"),
      ]);
  
      const arrowTopStyles = removeDuplicateCss([
        cssProps("width", styles.arrowWidth, "px"),
        cssProps("height", styles.arrowHeight, "px"),
        cssProps(
          "background-image",
          hasValidCSSValue(
            styles.scrollUpArrowbg
          )
            ? `url("${styles.scrollUpArrowbg}")`
            : undefined
        ),
        cssProps("background-repeat", "no-repeat"),
        cssProps("right", 0, "px"),
        cssProps("background-color", "lightblue"),
        cssProps("position", "absolute"),
        cssProps("z-index", 20),
        cssProps("background-position", "center"),
      ]);
  
      const arrowBottomStyles =
        removeDuplicateCss([
          cssProps("width", styles.arrowWidth, "px"),
          cssProps("height", styles.arrowHeight, "px"),
          cssProps("right", 0, "px"),
          cssProps(
            "background-image",
            hasValidCSSValue(
              styles.scrollDownArrowbg
            )
              ? `url("${styles.scrollDownArrowbg}")`
              : undefined
          ),
          cssProps("background-repeat", "no-repeat"),
          cssProps("background-color", "lightblue"),
          cssProps("position", "absolute"),
          cssProps("transform", "scaleY(-1)"),
          cssProps("bottom", 0, "px"),
          cssProps("z-index", 20),
          cssProps("background-position", "center"),
        ]);
  
      const cssBlocks = [
        mainTextStyles
          ? `#${layerData.id} {
              ${mainTextStyles}
            }`
          : "",
  
        isiInfoStyles
          ? `#${layerData.id}ISIInfo {
              ${isiInfoStyles}
            }`
          : "",
  
        expanCloseCSS,
  
        viewportStyles
          ? `#${layerData.id}viewport {
              ${viewportStyles}
            }`
          : "",
  
        overviewStyles
          ? `#${layerData.id}overview {
              ${overviewStyles}
            }`
          : "",
  
        scrollbarStyles
          ? `#scrollbar${layerData.id} {
              ${scrollbarStyles}
            }`
          : "",
  
        trackStyles
          ? `#${layerData.id}track {
              ${trackStyles}
            }`
          : "",
  
        thumbStyles
          ? `#${layerData.id}thumb {
              ${thumbStyles}
            }`
          : "",
  
        arrowTopStyles
          ? `#${layerData.id}arrowTop {
              ${arrowTopStyles}
            }`
          : "",
  
        arrowBottomStyles
          ? `#${layerData.id}arrowBottom {
              ${arrowBottomStyles}
            }`
          : "",
      ]
        .filter(Boolean)
        .join("\n\n");
  
      CssCollect += cssBlocks
        ? `\n${cssBlocks}`
        : "";
    }
  
    break;
  }
    
    case "Image":
      const exportHitAreas = sanitizeHitAreasForExport(styles?.hitAreas, styles);
      const hitAreaOverlayHTML = renderHitAreaOverlays(exportHitAreas, styles);
      const hasImageLink = Boolean(normalizeTargetLinkForExport(styles?.targetLink));
      const hasExportHitAreas = exportHitAreas.length > 0;
    
      const isImagePointerEventsEnabled = hasImageLink;
    
      const imageCursorPointer = isImagePointerEventsEnabled
        ? "pointer"
        : "default";
    
      if (flag) {
        if (Object.keys(set).length) {
          newLeft = set.x;
          newTop = set.y;
    
          newOpacity =
            styles.compOpacity === 100
              ? set?.opacity
              : styles.compOpacity / 100;
    
          newRotation = set?.rotation;
          newTransform = set?.transform;
        } else {
          newLeft = styles.left;
          newTop = styles.top;
    
          newOpacity =
            styles.compOpacity === 100
              ? set?.opacity
              : styles.compOpacity / 100;
    
          newRotation = set?.rotation;
          newTransform = set?.transform;
        }
    
        DOMConstruct =
          popUpOptions?.skewAnimation === true
            ? `<div class="skew1" id="${layerData.id}" >
            <div class="skew1imgbg"></div>
            ${hitAreaOverlayHTML}
          </div>`
            : `<div id='${layerData.id}'>${hitAreaOverlayHTML}</div>`;
    
        const flagSkewImageStyles = removeDuplicateCss([
          cssProps(
            "background-image",
            exportData
              ? `url(${deriveExportAssetCssUrl(
                  styles?.background
                )})`
              : hasValidCSSValue(styles?.background)
                ? `url(${styles.background})`
                : undefined
          ),
        ]);
    
        const flagImageStyles = removeDuplicateCss([
          cssProps("position", "absolute"),
          cssProps("width", styles.width, "px"),
          cssProps("height", styles.height, "px"),
          cssProps("top", newTop, "px"),
          cssProps("left", newLeft, "px"),
          cssProps("z-index", styles.zIndex),
          cssProps(
            "background-color",
            styles.backgroundColor
          ),
          cssProps("border", styles.border),
    
          buildBorderRadius(
            styles.borderTopLeftRadius,
            styles.borderTopRightRadius,
            styles.borderBottomRightRadius,
            styles.borderBottomLeftRadius
          ),
    
          // Sized to the whole layer box, so it must be positioned from that box
          // too — the default padding-box origin pushes the picture under the
          // right and bottom border.
          cssProps("background-origin", "border-box"),

          cssProps(
            "background-size",
            hasValidCSSValue(styles.width) &&
              hasValidCSSValue(styles.height)
              ? `${styles.width}px ${styles.height}px`
              : undefined
          ),

          cssProps(
            "background-image",
            hasValidCSSValue(
              exportData
                ? deriveExportAssetCssUrl(
                    styles?.background
                  )
                : styles?.background
            )
              ? `url(${
                  exportData
                    ? deriveExportAssetCssUrl(
                        styles?.background
                      )
                    : styles?.background
                })`
              : undefined
          ),
    
          cssProps(
            "background-repeat",
            styles.backgroundRepeat
          ),
    
          buildTransform(
            newRotation,
            newTransform
          ),
    
          cssProps("opacity", newOpacity),
    
          cssProps(
            "pointer-events",
            isImagePointerEventsEnabled
              ? "auto"
              : "none"
          ),
        ]);
    
        const flagImageCssBlock =
          popUpOptions?.skewAnimation === true
            ? flagSkewImageStyles
              ? `#${layerData.id} {
      ${flagSkewImageStyles}
    }`
              : ""
            : flagImageStyles
              ? `#${layerData.id} {
      ${flagImageStyles}
    }`
              : "";
    
        CssCollect += flagImageCssBlock
          ? `\n${flagImageCssBlock}`
          : "";
    
        CssCollect += buildHitAreaCssRules(
          exportHitAreas,
          animationObject
        );
    
        img.push(styles.background);
      } else {
        if (Object.keys(set).length) {
          newLeft = set.x;
          newTop = set.y;
    
          newOpacity =
            styles.compOpacity === 100
              ? set?.opacity
              : styles.compOpacity / 100;
    
          newRotation = set?.rotation;
          newTransform = set?.transform;
        } else {
          newLeft = styles.left;
          newTop = styles.top;
    
          newOpacity =
            styles.compOpacity === 100
              ? set?.opacity
              : styles.compOpacity / 100;
    
          newRotation = set?.rotation;
          newTransform = set?.transform;
        }
    
        let imageLink = exportData
          ? deriveExportAssetCssUrl(styles.background)
          : styles.background;
    
        DOMConstruct =
          popUpOptions?.skewAnimation === true
            ? `<div class="skew1" id="${layerData.id}" >
          <div class="skew1imgbg"></div>
          ${hitAreaOverlayHTML}
        </div>`
            : `<div id='${layerData.id}'>${hitAreaOverlayHTML}</div>`;
    
        const skewImageStyles = removeDuplicateCss([
          cssProps(
            "background-image",
            hasValidCSSValue(imageLink)
              ? `url(${imageLink})`
              : undefined
          ),
        ]);
    
        const imageStyles = removeDuplicateCss([
          cssProps("position", "absolute"),
          cssProps("width", styles.width, "px"),
          cssProps("height", styles.height, "px"),
          cssProps("top", newTop, "px"),
          cssProps("left", newLeft, "px"),
          cssProps("z-index", styles.zIndex),
          cssProps("background-color", styles.backgroundColor),
          cssProps("border", styles.border),
          buildBorderRadius(
            styles.borderTopLeftRadius,
            styles.borderTopRightRadius,
            styles.borderBottomRightRadius,
            styles.borderBottomLeftRadius
          ),
          // Sized to the whole layer box, so it must be positioned from that box
          // too — the default padding-box origin pushes the picture under the
          // right and bottom border.
          cssProps("background-origin", "border-box"),
          cssProps(
            "background-size",
            hasValidCSSValue(styles.width) &&
              hasValidCSSValue(styles.height)
              ? `${styles.width}px ${styles.height}px`
              : undefined
          ),
          cssProps(
            "background-image",
            hasValidCSSValue(imageLink)
              ? `url(${imageLink})`
              : undefined),
          cssProps("background-repeat", styles.backgroundRepeat),
          buildTransform(newRotation, newTransform),
          cssProps("opacity", newOpacity),
          cssProps("cursor", imageCursorPointer),
          cssProps(
            "pointer-events",
            isImagePointerEventsEnabled
              ? "auto"
              : "none"
          ),
        ]);
    
        const imageCssBlock = [
          popUpOptions?.skewAnimation === true
            ? skewImageStyles
              ? `#${layerData.id} {
                  ${skewImageStyles}
                }`
              : ""
            : imageStyles
              ? `#${layerData.id} {
                  ${imageStyles}
                }`
              : "",
        ].filter(Boolean).join("\n\n");
        
        CssCollect += imageCssBlock
          ? `\n${imageCssBlock}`
          : "";
        
        const hitAreaCssRules = buildHitAreaCssRules(
          exportHitAreas,
          animationObject
        );
        
        CssCollect += hitAreaCssRules
          ? `\n${hitAreaCssRules}`
          : "";
            
        img.push(styles.background);
      }
    
    break;
    case "Button":
      if (Object.keys(set).length) {
        newLeft = set.x;
        newTop = set.y;
        newOpacity = set?.opacity;
        newRotation = set?.rotation;
        newTransform = set?.transform;
      } else {
        newLeft = styles.left;
        newTop = styles.top;
        newOpacity = set?.opacity;
        newRotation = set?.rotation;
        newTransform = set?.transform;
      }
    
      let textDecorationvalue = styles?.underlineClick
        ? styles?.strikeThroughClicked
          ? styles?.textDecUnderline + " " + styles?.textDecoration
          : styles?.textDecUnderline
        : styles?.textDecoration;
    
      const buttonJustify =
        styles.textAlign === "left"
          ? "start"
          : styles.textAlign === "right"
            ? "end"
            : "center";
      DOMConstruct = `<button id='${layerData.id}'><div class="btn-text">${
        layerData.froalaText || ""
      }</div></button>`;
    
      const buttonStyles = removeDuplicateCss([
        cssProps("position", "absolute"),
        cssProps("width", styles.width, "px"),
        cssProps("height", styles.height, "px"),
        cssProps("top", newTop, "px"),
        cssProps("left", newLeft, "px"),
        cssProps("font-weight", styles.fontWeight),
        cssProps(
          "font-family",
          normalizeFontFamily(styles.fontFamily)
        ),
        cssProps("color", styles.color),
        cssProps("line-height", styles.lineHeight),
        cssProps("z-index", styles.zIndex),
        cssProps("letter-spacing", styles.letterSpacing, "px"),
        cssProps("font-size", styles.fontSize, "px"),
        cssProps("background-color", styles.backgroundColor),
        cssProps("border", styles.border),
    
        buildBorderRadius(
          styles.borderTopLeftRadius,
          styles.borderTopRightRadius,
          styles.borderBottomRightRadius,
          styles.borderBottomLeftRadius
        ),
    
        cssProps("text-align", styles.textAlign),
        cssProps("display", "inline-grid"),
        cssProps("justify-content", buttonJustify),
        cssProps("align-items", "center"),
        cssProps("white-space", "normal"),
    
        buildPadding(
          styles.paddingTop,
          styles.paddingRight,
          styles.paddingBottom,
          styles.paddingLeft
        ),
    
        cssProps("word-wrap", "break-word"),
        cssProps("overflow", "hidden"),
    
        buildTransform(newRotation, newTransform),
    
        cssProps("opacity", newOpacity),
    
        cssProps(
          "cursor",
          styles.targetLink !== "" ? "pointer" : "default"
        ),
    
        cssProps("font-style", styles?.fontStyle),
        cssProps("text-transform", styles?.textTransform),
        cssProps("text-decoration", textDecorationvalue),
        cssProps("text-align", styles?.textAlign),
      ]);
    
      const buttonCssBlock = [
        buttonStyles
          ? `#${layerData.id} {
              ${buttonStyles}
            }`
          : "",
        `#${layerData.id} > .btn-text { width: 100%; max-width: 100%; box-sizing: border-box; }`,
        `#${layerData.id} p { margin: 0; padding: 0; }`,
      ]
        .filter(Boolean)
        .join("\n\n");
    
      CssCollect += buttonCssBlock
        ? `\n${buttonCssBlock}\n\n p {line-height: normal;}`
        : "";
    
      CssCollect = CssCollect.replaceAll("pxpx", "px");
    
    break;
    case "Shape":
      if (Object.keys(set).length) {
        newLeft = set.x;
        newTop = set.y;
        newOpacity =
          styles.compOpacity === 100
            ? set?.opacity
            : styles.compOpacity / 100;
        newRotation = set?.rotation;
        newTransform = set?.transform;
      } else {
        newLeft = styles.left;
        newTop = styles.top;
        newOpacity =
          styles.compOpacity === 100
            ? set?.opacity
            : styles.compOpacity / 100;
        newRotation = set?.rotation;
        newTransform = set?.transform;
      }
    
      const isFullCanvasShape = isShapeMatchingCanvasSize(
        styles,
        canvasDimension,
      );

      if (
        layerData.textComponentType?.addContent ===
        "triangleShape"
      ) {
        const triangleSrc = isFullCanvasShape
          ? buildFillOnlySvgSrc(layerData.triImage)
          : layerData.triImage;
        DOMConstruct = `<div id='${layerData.id}'><img src="${triangleSrc}" class="triangleImage" style="width:${styles.width}px; height:${styles.height}px" width="${styles.width}" height="${styles.height}"/></div>`;
    
        const triangleStyles = removeDuplicateCss([
          cssProps("position", "absolute"),
          cssProps("width", styles.width, "px"),
          cssProps("height", styles.height, "px"),
          cssProps("top", newTop, "px"),
          cssProps("left", newLeft, "px"),
          cssProps("z-index", styles.zIndex),
          cssProps(
            "pointer-events",
            styles.targetLink === "https://" ||
              styles.targetLink === ""
              ? "none"
              : undefined
          ),
          buildTransform(newRotation, newTransform),
          cssProps("opacity", newOpacity),
        ]);
    
        const triangleCssBlock = [
          triangleStyles
            ? `#${layerData.id} {
                ${triangleStyles}
              }`
            : "",
        ]
          .filter(Boolean)
          .join("\n\n");
    
        CssCollect += triangleCssBlock
          ? `\n${triangleCssBlock}`
          : "";
        if (isFullCanvasShape) {
          const triangleStroke = buildShapeStrokeFrame({
            layerId: layerData.id,
            styles,
            newLeft,
            newTop,
            newOpacity,
            newRotation,
            newTransform,
            kind: "svg",
            svgSrc: layerData.triImage,
          });
          DOMConstruct += triangleStroke.dom;
          CssCollect += triangleStroke.css;
        }
      } else if (
        layerData.textComponentType?.addContent ===
        "polygonShape"
      ) {
        const polygonSrc = isFullCanvasShape
          ? buildFillOnlySvgSrc(layerData.pentImage)
          : layerData.pentImage;
        DOMConstruct = `<div id='${layerData.id}'><img src="${polygonSrc}" class="pentagonImage" style="width:${styles.width}px; height:${styles.height}px"/></div>`;
    
        const polygonStyles = removeDuplicateCss([
          cssProps("position", "absolute"),
          cssProps("width", styles.width, "px"),
          cssProps("height", styles.height, "px"),
          cssProps("top", newTop, "px"),
          cssProps("left", newLeft, "px"),
          cssProps("z-index", styles.zIndex),
          cssProps(
            "pointer-events",
            styles.targetLink === "https://" ||
              !styles.targetLink
              ? "none"
              : "auto"
          ),
          buildTransform(newRotation, newTransform),
          cssProps("opacity", newOpacity),
        ]);
    
        const polygonCssBlock = [
          polygonStyles
            ? `#${layerData.id} {
                ${polygonStyles}
              }`
            : "",
        ]
          .filter(Boolean)
          .join("\n\n");
    
        CssCollect += polygonCssBlock
          ? `\n${polygonCssBlock}`
          : "";
        if (isFullCanvasShape) {
          const polygonStroke = buildShapeStrokeFrame({
            layerId: layerData.id,
            styles,
            newLeft,
            newTop,
            newOpacity,
            newRotation,
            newTransform,
            kind: "svg",
            svgSrc: layerData.pentImage,
          });
          DOMConstruct += polygonStroke.dom;
          CssCollect += polygonStroke.css;
        }
      } else if (
        layerData.textComponentType?.addContent ===
        "hexagonShape"
      ) {
        const hexagonSrc = isFullCanvasShape
          ? buildFillOnlySvgSrc(layerData.hexImage)
          : layerData.hexImage;
        DOMConstruct = `<div id='${layerData.id}'><img src="${hexagonSrc}" style="width:${styles.width}px; height:${styles.height}px"/></div>`;
    
        const hexagonStyles = removeDuplicateCss([
          cssProps("position", "absolute"),
          cssProps("width", styles.width, "px"),
          cssProps("height", styles.height, "px"),
          cssProps("top", newTop, "px"),
          cssProps("left", newLeft, "px"),
          cssProps("z-index", styles.zIndex),
          cssProps(
            "pointer-events",
            !styles.targetLink ||
              styles.targetLink === "https://"
              ? "none"
              : "auto"
          ),
          buildTransform(newRotation, newTransform),
          cssProps("opacity", newOpacity),
        ]);
    
        const hexagonCssBlock = [
          hexagonStyles
            ? `#${layerData.id} {
                ${hexagonStyles}
              }`
            : "",
        ]
          .filter(Boolean)
          .join("\n\n");
    
        CssCollect += hexagonCssBlock
          ? `\n${hexagonCssBlock}`
          : "";
        if (isFullCanvasShape) {
          const hexagonStroke = buildShapeStrokeFrame({
            layerId: layerData.id,
            styles,
            newLeft,
            newTop,
            newOpacity,
            newRotation,
            newTransform,
            kind: "svg",
            svgSrc: layerData.hexImage,
          });
          DOMConstruct += hexagonStroke.dom;
          CssCollect += hexagonStroke.css;
        }
      } else if (
        layerData.textComponentType?.addContent ===
        "rectangleShape"
      ) {
        DOMConstruct = `<div id='${layerData.id}'></div>`;
    
        const rectangleStyles = removeDuplicateCss([
          cssProps("position", "absolute"),
          cssProps("width", styles.width, "px"),
          cssProps("height", styles.height, "px"),
          cssProps("box-sizing", "border-box !important"),
          cssProps("top", newTop, "px"),
          cssProps("left", newLeft, "px"),
          cssProps("z-index", styles.zIndex),
          cssProps("background-color", styles.backgroundColor),
          cssProps(
            "pointer-events",
            styles.targetLink === "https://" ||
              styles.targetLink === ""
              ? "none"
              : "auto"
          ),
          isFullCanvasShape ? "" : cssProps("border", styles.border),
          buildBorderRadius(
            styles.borderTopLeftRadius,
            styles.borderTopRightRadius,
            styles.borderBottomRightRadius,
            styles.borderBottomLeftRadius
          ),
          buildPadding(
            styles.paddingTop,
            styles.paddingRight,
            styles.paddingBottom,
            styles.paddingLeft
          ),
          buildTransform(newRotation, newTransform),
          cssProps("opacity", newOpacity),
        ]);
    
        const rectangleCssBlock = [
          rectangleStyles
            ? `#${layerData.id} {
                ${rectangleStyles}
              }`
            : "",
        ]
          .filter(Boolean)
          .join("\n\n");
    
        CssCollect += rectangleCssBlock
          ? `\n${rectangleCssBlock}`
          : "";
        if (isFullCanvasShape) {
          const rectangleStroke = buildShapeStrokeFrame({
            layerId: layerData.id,
            styles,
            newLeft,
            newTop,
            newOpacity,
            newRotation,
            newTransform,
            kind: "css",
            shapeType: "rectangleShape",
          });
          DOMConstruct += rectangleStroke.dom;
          CssCollect += rectangleStroke.css;
        }
      } else if (
        layerData.textComponentType?.addContent ===
        "circleShape"
      ) {
        DOMConstruct = `<div id='${layerData.id}'></div>`;
    
        const circleStyles = removeDuplicateCss([
          cssProps("position", "absolute"),
          cssProps("width", styles.width, "px"),
          cssProps("height", styles.height, "px"),
          cssProps("box-sizing", "border-box !important"),
          cssProps("top", newTop, "px"),
          cssProps("left", newLeft, "px"),
          cssProps("z-index", styles.zIndex),
          cssProps("background-color", styles.backgroundColor),
          isFullCanvasShape ? "" : cssProps("border", styles.border),
          cssProps(
            "pointer-events",
            styles.targetLink === "https://" ||
              styles.targetLink === ""
              ? "none"
              : "auto"),
          cssProps("border-radius", styles.borderRadius),
          cssProps("padding-top", styles.paddingTop, "px"),
          buildTransform(newRotation, newTransform),
          cssProps("opacity", newOpacity),
        ]);
    
        const circleCssBlock = [
          circleStyles
            ? `#${layerData.id} {
                ${circleStyles}
              }`
            : "",
        ]
          .filter(Boolean)
          .join("\n\n");
    
        CssCollect += circleCssBlock
          ? `\n${circleCssBlock}`
          : "";
        if (isFullCanvasShape) {
          const circleStroke = buildShapeStrokeFrame({
            layerId: layerData.id,
            styles,
            newLeft,
            newTop,
            newOpacity,
            newRotation,
            newTransform,
            kind: "css",
            shapeType: "circleShape",
          });
          DOMConstruct += circleStroke.dom;
          CssCollect += circleStroke.css;
        }
      } else {
      }
    
      break;
    
    default:
      break;
  }
  return { DOMConstruct, CssCollect, trackHeight, thumbHeight };
}
